import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useHistory, useParams } from 'react-router';
import { CONSTANTS, refMapper } from 'services';
import { StaticContentClient, classer } from 'utils';
import { BUSINESS_UNIT } from 'services/constants';
import { useMobileLayout, useTranslations } from 'utils/hooks';
import { Drawer, Content, DownloadButton, LogoIcon } from '../';
import { CloseButton, HamburgerButton } from './shared';
import { NavLink } from 'react-router-dom';

import classes from './Layout.module.scss';

const { breakpointMobile } = CONSTANTS;

const isHamburgerShown = window.innerWidth < breakpointMobile;

const rerouteToHome = (businessUnit, language) => {
  window.location.href = `${
    window.location.origin
  }/${businessUnit}/${language.toUpperCase()}`;
};

const Layout = ({ menuContent, selectedItemName }) => {
  const translations = useTranslations();
  const drawerContentNotEmpty = menuContent !== null && menuContent.length > 0;
  const { bu, lang, section: sectionFromParams } = useParams();
  const [content, setContent] = useState(null);
  const [contentSubpages, setContentSubpages] = useState(null);
  const [contentRendered, setContentRendered] = useState(false);
  const [downloadable, setDownloadable] = useState(false);
  const [mobileLayout, setMobileLayout] = useState(isHamburgerShown);
  const [drawerShow, setDrawerShow] = useState(false);
  const history = useHistory();
  const homepageRedirect = `/${BUSINESS_UNIT}/${lang}`;

  const fetchContent = useCallback(
    async pageName => {
      setContentSubpages(null);
      try {
        const response = await StaticContentClient.getContentData({
          pageName,
          lang,
          businessUnit: bu
        });
        const data = response.getData();

        if (response.isNotEmpty()) {
          const displayPage =
            data.translations.find(({ language }) => language === lang) ||
            data.translations[0];
          const isInactivePage = displayPage?.active === false;
          const noContentToDisplay =
            isInactivePage ||
            ((
              !displayPage.content ||
              displayPage.content === '' ||
              // workaround '<p></p>' <-- content manager automatically append empty <p> on no content
              displayPage.content === '<p></p>'
            ) && (!data.childPages || data.childPages.length === 0));

          if (noContentToDisplay) {
            return rerouteToHome(bu, lang);
          }
          setDownloadable(data.downloadable);
          setContent(displayPage);
          setContentSubpages(data.childPages);
        }
      } catch (err) {
        // TODO handle error on prod
        console.error(
          'Something went wrong when trying to fetch content %s',
          err
        );
      }
    },
    [bu, lang]
  );

  const parentSelected = useMemo(() => {
    if (!menuContent) {
      return null;
    }
    let parentElement;
    parentElement =
      menuContent.find(section => {
        if (!section.pages) {
          return null;
        }
        return section.pages.some(page => page.name === selectedItemName);
      }) ?? null;
    return parentElement;
  }, [menuContent, selectedItemName]);

  const toggleDrawerShow = useCallback(
    () => setDrawerShow(!drawerShow),
    [drawerShow]
  );

  const closeWindow = useCallback(() => {
    window.close();
  }, []);

  const goToLink = useCallback(
    (config = {}) => {
      const {
        bu: businessUnit = bu,
        section: selectedSection = sectionFromParams
      } = config;
      return history.push(`/${businessUnit}/${selectedSection}/${lang}`);
    },
    [bu, history, lang, sectionFromParams]
  );

  const autoClickFirstItem = useCallback(() => {
    const firstOption = refMapper.getRef(menuContent?.[0]?.name);
    if (contentRendered && !sectionFromParams && firstOption !== null) {
      // If first option has child pages we want to click the child page
      if (menuContent?.[0].pages?.length > 0) {
        goToLink({
          section: menuContent[0].pages[0].name,
          bu,
          lang
        });
        return;
      }
      firstOption.click();
    }
  }, [menuContent, goToLink, bu, lang, contentRendered, sectionFromParams]);

  useEffect(() => {
    typeof selectedItemName === 'string' && fetchContent(selectedItemName);
    /**
     * DO NOT Update these dependencies!!!
     * Dependenencies must be:
     * [lang, selectedItemName, drawerContentNotEmpty, menuContent ]
     * for this effect to work
     * Dependencies should not contain 'fetchContent' because it changes on every re-render :/
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerContentNotEmpty, lang, menuContent, selectedItemName]);

  useEffect(() => {
    autoClickFirstItem();
  }, [autoClickFirstItem]);

  useMobileLayout(setMobileLayout);

  return (
    <div
      className={classer([
        classes.Layout,
        mobileLayout && classes.MobileLayout
      ])}
    >
      <div className={classer([classes.SideTab, drawerShow && classes.Show])}>
        {mobileLayout ? (
          <>
            <div className={classes.TopButtonsRow}>
              <button
                type="button"
                className={classes.MobileCloseWindow}
                onClick={closeWindow}
              >
                {translations('closeWindow')}
              </button>
              <CloseButton onClick={toggleDrawerShow} />
            </div>
            <div className={classes.Logo}>
              <NavLink className={classes.LogoLink} to={homepageRedirect}>
                <LogoIcon />
              </NavLink>
            </div>
          </>
        ) : (
          <NavLink className={classes.Logo} to={homepageRedirect}>
            <div className={classes.LogoContainer}>
              <LogoIcon />
            </div>
          </NavLink>
        )}
        <div className={classes.OptionsTree}>
          {drawerContentNotEmpty ? (
            <Drawer
              menuOptions={menuContent}
              setContentRendered={setContentRendered}
              hideDrawer={toggleDrawerShow}
            />
          ) : null}
        </div>
      </div>
      {mobileLayout ? <HamburgerButton onClick={toggleDrawerShow} /> : null}
      <Content
        content={content}
        parentSelected={parentSelected}
        contentSubpages={contentSubpages}
        onContentClick={
          mobileLayout && drawerShow ? toggleDrawerShow : undefined
        }
      />
      {/* for now only Romanian market should be able to download */}
      {downloadable && bu === CONSTANTS.Romania && drawerContentNotEmpty ? (
        <DownloadButton />
      ) : null}
    </div>
  );
};

export default memo(Layout);
